import { Component, Element, Host, Prop, State, h } from '@stencil/core';
import { Consumer } from '@sydle/ui-utils';
import { brandIcons } from './brand-icons';

export type IconProviderFn = (name: string) => { name: string; style: { [key: string]: any } };

@Component({
  tag: 'sy-icon',
  styleUrl: 'scss/index.scss',
  shadow: true,
})
export class Icon {
  private consumer: Consumer;

  @Element() el: HTMLSyIconElement;

  /**
   * Name of the icon that will be used.
   */
  @Prop({ reflect: true }) name: string;

  /**
   * Custom aria-label for the icon
   */
  @Prop({ reflect: true }) accessibleLabel: string;

  /**
   * Icon's color.
   */
  @Prop({ reflect: true }) color: 'primary' | 'secondary' | 'error' | 'warning' | 'success' | 'info';

  @State() iconProviderFn: IconProviderFn | undefined;

  componentWillLoad() {
    this.consumer = new Consumer(this.el);
    this.consumer.subscribe(
      'iconProvider',
      (iconProviderFn: IconProviderFn | undefined) => {
        this.iconProviderFn = iconProviderFn;
      },
      { suppress: true },
    );
  }

  disconnectedCallback() {
    this.consumer.disconnect();
  }

  private getIconStyles() {
    const providedIcon = this.iconProviderFn ? this.iconProviderFn(this.name) : null;

    const name = providedIcon?.name || this.name;
    return { '--icon-name': `'${name}'`, ...(providedIcon?.style || {}) };
  }

  render() {
    const accessibilityAttributes = {
      role: 'img',
      'aria-label': this.accessibleLabel,
      'aria-hidden': this.accessibleLabel ? null : 'true',
    };

    if (this.name.startsWith('sy-brand-')) {
      const iconName = this.name.replace('sy-brand-', '');
      return (
        <Host {...accessibilityAttributes}>
          <div class="sy-brand-icon-wrapper" innerHTML={brandIcons[iconName]}></div>
        </Host>
      );
    }

    return <Host {...accessibilityAttributes} style={this.getIconStyles()}></Host>;
  }
}
